import React, {useMemo} from 'react';
import {TypeImplementation, withSystem} from '@packages/systems/wfdl';
import {
  annotate,
  ContextualExpression,
  ContextualExpressions,
  EDistinct as ContextualEDistinct,
  getAnn,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {DynListCuratedSettings} from './DynListCuratedSettings';
import {selectCollectionState} from '@packages/systems/cms/client-selectors';
import {getDefaultForComponentProp} from '../dataTypeUtils';
import {selectCurrentComponent} from '@packages/utilities/platform-selectors';
import {isCommerceCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {
  getCollectionListPropTypeName,
  createCollectionListPropType,
} from '@packages/systems/dynamo/utils/CollectionListPropNameUtils';

const selectPluginsFromState = (state: DesignerStores) =>
  state.DesignerStore.plugins;

export const CollectionListCuratedRender =
  ({collectionId}: {collectionId: string}) =>
  ({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: TypeImplementation.ExpressionEditorDispatch;
  }) => {
    return (
      <CollectionListCuratedComponent
        expr={expr}
        dispatch={dispatch}
        collectionId={collectionId}
      />
    );
  };

const CollectionListCuratedComponent = ({
  expr,
  dispatch,
  collectionId,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
  collectionId: string;
}) => {
  const collectionState = useDesignerSelector(selectCollectionState);
  const collection = collectionState.collections.get(collectionId);
  const plugins = useDesignerSelector(selectPluginsFromState);
  const currentComponent = useDesignerSelector(selectCurrentComponent);

  const defaultValueExpr = useMemo(
    () => getDefaultForComponentProp(plugins, currentComponent, stripAll(expr)),
    [plugins, currentComponent, expr]
  );

  const exprAsUnique = useMemo(() => {
    // If expression is already a binding, use it as-is.
    if (ContextualExpressions.isBinding(stripAnn(expr))) {
      return expr;
    }

    // Otherwise, wrap it in a Distinct expression to ensure proper type
    // checking and binding capabilities for the curated data.
    const ann = getAnn(expr);

    return annotate(
      ContextualEDistinct({
        name: getCollectionListPropTypeName('Curated', collectionId),
        value: expr,
      }),
      {
        ...ann,
        env: {
          ...ann.env,
          // Ensure there is no duplicate `in distinct` key
          expressionPath: ann.env.expressionPath.slice(0, -1),
        },
        type: withSystem(
          {
            isBindable: !isCommerceCollection(collection),
            label: 'Select items',
            labelVariant: 'hidden',
          },
          createCollectionListPropType('Curated', collectionId)
        ),
      }
    );
  }, [expr, collection]);

  return (
    <DynListCuratedSettings
      collectionId={collectionId}
      defaultValueExpr={defaultValueExpr}
      expr={expr}
      exprAsUnique={exprAsUnique}
      expressionEditorDispatch={dispatch}
    />
  );
};
